import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Caption1,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { formatTime, parsePrFailure, type PrCreationStep } from '@pr-app/shared';
import { getRequest, type RequestAuditEntry, type RequestDetail } from '../api/requests';
import { notifyRequester, retryPrCreation, sendBackToRequester } from '../api/review';
import { ActionBar } from '../components/ActionBar';
import { DataCard, DataCardList } from '../components/DataCardList';
import { PageHeader } from '../components/PageHeader';
import { Section } from '../components/Section';
import { StatusBadge } from '../components/StatusBadge';
import { useIsNarrowViewport } from '../hooks/useIsNarrowViewport';
import { useResponsiveStyles } from '../hooks/useResponsiveStyles';
import { useRestoreFocusOnClose } from '../hooks/useRestoreFocusOnClose';
import { plainFailureHints } from '../lib/prFailureNotice';
import { SPACE } from '../styles/scale';
import { useLayoutStyles, useSurfaceStyles } from '../styles/surfaces';

/**
 * Wireframe 05.2, frame V7 — "PR creation failed · retry and recovery" (#46).
 *
 * A state the SOW does not describe but which will happen: the approval was
 * recorded, D365 refused the requisition, and without this screen the request
 * would sit APPROVED forever with nobody the wiser until procurement asked.
 *
 * Three things the sheet is specific about, all load-bearing:
 *
 * - **The raw D365 message, verbatim** (note 3). Whoever fixes this is a D365
 *   administrator, and "Something went wrong" wastes their time. The same text
 *   is in the admin audit log, because both read the same `PR_FAILED` rows —
 *   there is no separate attempts table.
 * - **The approval stands** (note 2). Nothing here can un-approve the request;
 *   only "Send back to requester" changes its status, and that is a deliberate
 *   rejection with the D365 error as its reason (note 4).
 * - **Nothing has notified anyone** (note 6), which is why "Notify requester"
 *   is a button rather than something that already happened.
 *
 * Retry is safe to press twice: the server checks D365 for an existing
 * requisition before writing (#45). Nothing in this component may imply
 * otherwise — no "are you sure this hasn't already worked?" hedging.
 *
 * ── Design system pass (#202) ──
 * The title is a `PageHeader` (`Title3` as a real `<h1>`) rather than a bare
 * `Title2` span — 28/36 is the stat-tile size, and a span is not a heading to a
 * screen reader. The attempt log is a `Section`, and the three V7 actions are
 * one `ActionBar`: sticky and last in DOM order, so a keyboard user reads what
 * D365 said before reaching Retry (K1), and "← Queue" is that bar's leading,
 * lowest-emphasis action instead of a loose button above the title.
 */

const useStyles = makeStyles({
  /**
   * The D365 text is a machine message, so it is a `<code>` element — the
   * monospace face comes from the element itself, not from a font declaration
   * here (`fontFamily:` is a banned declaration; the type ramp is the theme's).
   * `useSurfaceStyles().inset` supplies the E0 panel it sits in — a panel
   * nested inside a card, at the 4px control radius — and `anywhere` wrapping
   * stops a long OData message widening the tab canvas.
   */
  rawError: {
    display: 'block',
    marginTop: SPACE.xs,
    overflowWrap: 'anywhere',
  },
  /** Inside a `MessageBarBody` there is no stack to own the gap, so these two say their own. */
  bannerLine: { display: 'block', marginTop: SPACE.xs },
  /** The attempt table and the note under it. */
  sectionStack: { display: 'flex', flexDirection: 'column', gap: SPACE.s, minWidth: '0' },
  hint: { display: 'block', color: tokens.colorNeutralForeground2 },
  resultCell: { overflowWrap: 'anywhere' },
});

/** One row of the attempt history — sourced from the request's own audit trail. */
interface Attempt {
  number: number;
  at: string;
  result: string;
  failed: boolean;
  /** Which sub-step failed (#44) — null on a success row, or a failure recorded before steps existed. */
  step: PrCreationStep | null;
}

const ATTEMPT_ACTIONS = new Set(['PR_FAILED', 'PR_CREATED']);

/**
 * Both halves matter, and the status half is what keeps #132 correct: a
 * request whose requisition exists but whose files did not attach is
 * PR_CREATED, carries a `PR_ATTACHMENTS_FAILED` row rather than a `PR_FAILED`
 * one, and must **not** land on this screen — there is nothing here for it to
 * retry, and calling a live requisition a failure is the exact confusion the
 * separate audit action exists to prevent. Its gap is visible in the audit
 * trail (badged "Attachments not linked") and in the request's own history.
 */
export function isPrCreationFailure(request: RequestDetail): boolean {
  return request.status === 'APPROVED' && request.auditLog.some((e) => e.action === 'PR_FAILED');
}

/**
 * Oldest attempt is number 1 — the sheet's own example counts up to the most
 * recent — but the list is shown newest first, which is the one someone opening
 * this screen wants.
 */
export function buildAttempts(auditLog: RequestAuditEntry[]): Attempt[] {
  return auditLog
    .filter((entry) => ATTEMPT_ACTIONS.has(entry.action))
    .toSorted((a, b) => a.createdAt.localeCompare(b.createdAt))
    .map((entry, index) => {
      const failed = entry.action === 'PR_FAILED';
      // The step tag is a prefix on the comment, not a replacement for it — it
      // comes off here so what is rendered stays D365's own words (V7 note 3).
      const parsed = parsePrFailure(entry.comment ?? '');
      return {
        number: index + 1,
        at: entry.createdAt,
        result: (failed ? parsed.message : entry.comment) || (failed ? 'Failed' : 'Created'),
        failed,
        step: failed ? parsed.step : null,
      };
    })
    .toReversed();
}

/** #148 — both of these are time-of-day-only labels on the same attempt log (a full
 * date is never needed: an attempt list is read the day it happened), so both now
 * share the one 24-hour `formatTime` the rest of the app uses — the previous
 * `timeLabel` additionally carried seconds and an AM/PM locale default, which is
 * exactly the ambiguity-prone, browser-locale formatting this ticket removes. */
function timeLabel(iso: string): string {
  return formatTime(iso);
}

function shortTimeLabel(iso: string): string {
  return formatTime(iso);
}

type LoadState =
  | { phase: 'loading' }
  | { phase: 'ready'; request: RequestDetail }
  | { phase: 'error'; message: string };

type PendingAction = 'retry' | 'sendBack' | null;

export function PrCreationFailure({
  requestId,
  onBack,
  onResolved,
}: {
  requestId: string;
  onBack: () => void;
  /** The request left this state — created in D365, or sent back to the requester. */
  onResolved: () => void;
}) {
  const styles = useStyles();
  const layout = useLayoutStyles();
  const surfaces = useSurfaceStyles();
  const responsive = useResponsiveStyles();
  const isNarrow = useIsNarrowViewport();
  const [state, setState] = useState<LoadState>({ phase: 'loading' });
  const [dialog, setDialog] = useState<PendingAction>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notified, setNotified] = useState(false);

  // K1 "Dialog opens": both of these are confirm-style dialogs, so focus goes
  // to the confirming action — the same destination `ReviewerQueue` gives its
  // Approve dialog. Fluent would otherwise focus the first focusable child,
  // which is Cancel; and under jsdom it finds none at all and focuses the
  // surface, racing tabster's `aria-hidden` (log entry 21). Placing focus
  // explicitly settles both the spec and the race.
  const confirmRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (dialog !== null) confirmRef.current?.focus();
  }, [dialog]);

  // K1 "Dialog closes": back to the button that opened it — Retry creation or
  // Send back to requester — never to the top of the page.
  useRestoreFocusOnClose(dialog !== null);

  const load = useCallback(async () => {
    setState({ phase: 'loading' });
    try {
      setState({ phase: 'ready', request: await getRequest(requestId) });
    } catch (err) {
      setState({ phase: 'error', message: err instanceof Error ? err.message : String(err) });
    }
  }, [requestId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function runAction(action: () => Promise<'resolved' | 'stillFailing'>) {
    setBusy(true);
    setActionError(null);
    try {
      const result = await action();
      setDialog(null);
      if (result === 'resolved') {
        onResolved();
        return;
      }
      // Still failing: reload so the new attempt appears in the history and the
      // banner shows what D365 said this time, rather than the previous message.
      await load();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  const handleRetry = () =>
    runAction(async () => {
      const result = await retryPrCreation(requestId);
      return result.outcome === 'CREATED' ? 'resolved' : 'stillFailing';
    });

  const handleSendBack = () =>
    runAction(async () => {
      await sendBackToRequester(requestId);
      return 'resolved';
    });

  async function handleNotify() {
    setBusy(true);
    setActionError(null);
    setNotified(false);
    try {
      await notifyRequester(requestId);
      setNotified(true);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  /** One page shape for all three phases, so the gutter and rhythm cannot drift apart. */
  const pageClass = mergeClasses(layout.stack, responsive.touchTargets);

  if (state.phase === 'loading') {
    return (
      <div className={pageClass}>
        <Spinner label="Loading the request…" labelPosition="after" />
        <ActionBar back={{ label: '← Queue', onClick: onBack }} />
      </div>
    );
  }

  if (state.phase === 'error') {
    return (
      <div className={pageClass}>
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>Could not load this request</MessageBarTitle>
            {state.message}
          </MessageBarBody>
        </MessageBar>
        <ActionBar
          back={{ label: '← Queue', onClick: onBack }}
          primary={{ label: 'Try again', onClick: () => void load() }}
        />
      </div>
    );
  }

  const { request } = state;
  const attempts = buildAttempts(request.auditLog);
  const lastFailure = attempts.find((attempt) => attempt.failed);
  /**
   * The one case where "could not be created" would be a lie: the requisition
   * exists in D365 and only Document Management failed (#44). Saying otherwise
   * sends a D365 administrator looking for a requisition that is already there.
   *
   * Only reachable for attempts recorded **before** #132 — an attachment
   * failure no longer fails an attempt, so no new `PR_FAILED` row can be
   * tagged `ATTACHMENTS`. The audit trail is insert-only and the old rows
   * stay, so the wording stays with them.
   */
  const attachmentsOnly = lastFailure?.step === 'ATTACHMENTS';
  const approval = request.auditLog.find((entry) => entry.action === 'APPROVED');
  // Last, not first — mirrors `RequestForm.tsx`'s own rejection lookup: a
  // request can only be REJECTED once at a time, but if it were ever rejected,
  // resubmitted and rejected again, the newest entry is the one that matters.
  let rejection: RequestAuditEntry | undefined;
  for (const entry of request.auditLog) {
    if (entry.action === 'REJECTED') rejection = entry;
  }
  // #239 — this screen is only reachable from the queue's PR-failures list,
  // which is server-filtered to `status: 'APPROVED'` — but nothing stops the
  // request moving on (most commonly "Send back to requester") between that
  // list loading and this screen's own `GET /api/requests/:id` (a stale tab,
  // a reload, a second reviewer's screen open on the same request). The
  // status badge below is always correct; the banner and recovery actions
  // must agree with it rather than with the queue's stale filter.
  const isRecoverable = isPrCreationFailure(request);

  return (
    <div className={pageClass}>
      <PageHeader
        title={`PR-${String(request.requestNo)}`}
        status={<StatusBadge status={request.status} />}
        meta={
          request.status === 'APPROVED' && approval ? (
            <Caption1>
              Approved by {approval.actorName} · {shortTimeLabel(approval.createdAt)}
            </Caption1>
          ) : request.status === 'REJECTED' && rejection ? (
            <Caption1>
              Rejected by {rejection.actorName} · {shortTimeLabel(rejection.createdAt)}
            </Caption1>
          ) : undefined
        }
      />

      {isRecoverable && (
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>
              {attachmentsOnly
                ? 'The requisition was created in D365, but its attachments could not be linked.'
                : 'The Purchase Requisition could not be created in D365.'}
            </MessageBarTitle>
            {plainFailureHints(
              lastFailure?.result ?? '',
              'press Retry creation — no new request is needed',
            ).map((hint) => (
              <span key={hint} className={styles.bannerLine}>
                {hint}
              </span>
            ))}
            <span className={styles.bannerLine}>D365 returned:</span>
            {/* Verbatim, unedited — V7 note 3. */}
            <code className={mergeClasses(surfaces.inset, styles.rawError)}>
              {lastFailure?.result}
            </code>
            <span className={styles.bannerLine}>
              Attempted {attempts.length} time{attempts.length === 1 ? '' : 's'}
              {lastFailure ? `, last at ${shortTimeLabel(lastFailure.at)}` : ''}.{' '}
              {attachmentsOnly
                ? 'The approval and the requisition both stand — only the attachment step failed, and retrying links just the files that are still missing.'
                : 'The approval stands — only the D365 step failed.'}
            </span>
          </MessageBarBody>
        </MessageBar>
      )}

      {!isRecoverable && (
        <MessageBar intent="warning">
          <MessageBarBody>
            <MessageBarTitle>
              This request has moved on and is no longer actionable here.
            </MessageBarTitle>
            <span className={styles.bannerLine}>
              Its Purchase Requisition was never created in D365 — see the attempt history below —
              but the status badge above is now the current, authoritative state.
            </span>
          </MessageBarBody>
        </MessageBar>
      )}

      {isRecoverable && notified && (
        <MessageBar intent="success">
          <MessageBarBody>
            <MessageBarTitle>{request.requesterName} has been notified.</MessageBarTitle>
            They know the requisition has not been created yet.
          </MessageBarBody>
        </MessageBar>
      )}

      {isRecoverable && actionError && (
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>That did not work</MessageBarTitle>
            {actionError}
          </MessageBarBody>
        </MessageBar>
      )}

      <Section title="Attempt history" count={attempts.length}>
        <div className={styles.sectionStack}>
          {isNarrow ? (
            <DataCardList ariaLabel="Attempt history">
              {attempts.map((attempt) => (
                <DataCard
                  key={attempt.number}
                  heading={`Attempt ${String(attempt.number)}`}
                  fields={[
                    { label: 'Time', value: timeLabel(attempt.at) },
                    { label: 'Result', value: attempt.result },
                  ]}
                />
              ))}
            </DataCardList>
          ) : (
            <Table aria-label="Attempt history">
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>Attempt</TableHeaderCell>
                  <TableHeaderCell>Time</TableHeaderCell>
                  <TableHeaderCell>Result</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {attempts.map((attempt) => (
                  <TableRow key={attempt.number}>
                    <TableCell>{attempt.number}</TableCell>
                    <TableCell>{timeLabel(attempt.at)}</TableCell>
                    <TableCell className={styles.resultCell}>{attempt.result}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <Caption1 as="p" className={styles.hint}>
            Every attempt is in the admin audit log too, against this request.
          </Caption1>
        </div>
      </Section>

      {/*
        K1: the decision bar is LAST in DOM order, so a keyboard user reaches
        Retry only after reading what D365 said. Left to right, lowest emphasis
        first, exactly as the sheet draws them: ← Queue · Notify requester ·
        Send back to requester · Retry creation. `secondary` is an ARRAY rather
        than a fragment — `ActionBar` clones each child with `disabled` while
        busy, and a fragment would be cloned as one node with a prop it cannot
        take.
      */}
      {isRecoverable ? (
        <ActionBar
          sticky
          busy={busy}
          back={{ label: '← Queue', onClick: onBack }}
          secondary={[
            <Button key="notify" appearance="transparent" onClick={() => void handleNotify()}>
              Notify requester
            </Button>,
            <Button key="sendBack" appearance="secondary" onClick={() => setDialog('sendBack')}>
              Send back to requester
            </Button>,
          ]}
          primary={{ label: 'Retry creation', onClick: () => setDialog('retry') }}
        />
      ) : (
        // The request is no longer APPROVED — nothing on this screen can act
        // on it (retry would refuse it, send-back has already happened or no
        // longer applies). All that's left is the way back to the queue.
        <ActionBar sticky back={{ label: '← Queue', onClick: onBack }} />
      )}

      {isRecoverable && dialog && (
        <Dialog open onOpenChange={(_, data) => !data.open && setDialog(null)}>
          <DialogSurface className={responsive.touchTargets}>
            <DialogBody>
              {dialog === 'retry' ? (
                <>
                  <DialogTitle>Retry creating PR-{request.requestNo} in D365?</DialogTitle>
                  <DialogContent>
                    {attachmentsOnly
                      ? 'This attaches the missing files to the requisition that already exists. Files that were attached successfully are skipped, so retrying cannot duplicate one — but nothing has changed since the last attempt unless the problem was fixed in D365.'
                      : 'This sends the request to D365 again. It checks for an existing requisition first, so retrying cannot create a duplicate — but nothing has changed since the last attempt unless the data was fixed in D365.'}
                  </DialogContent>
                </>
              ) : (
                <>
                  <DialogTitle>Send PR-{request.requestNo} back to the requester?</DialogTitle>
                  <DialogContent>
                    The request returns to Rejected, with what D365 said as the reason, and{' '}
                    {request.requesterName} can fix and resubmit it. The approval is undone.
                  </DialogContent>
                </>
              )}
              <DialogActions>
                <Button appearance="secondary" disabled={busy} onClick={() => setDialog(null)}>
                  Cancel
                </Button>
                <Button
                  ref={confirmRef}
                  appearance="primary"
                  disabled={busy}
                  onClick={() => void (dialog === 'retry' ? handleRetry() : handleSendBack())}
                >
                  {busy ? (
                    <Spinner size="tiny" />
                  ) : dialog === 'retry' ? (
                    'Retry creation'
                  ) : (
                    'Send back'
                  )}
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      )}
    </div>
  );
}
